import type { FC } from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import SimpleLoader from "~/components/SimpleLoader"
import useProposalSavings from "~/containers/Proposal/state/savings/use-proposal-savings"
import {
  syncWaitingForEntityState,
  utilitySavingsPendingState,
} from "~/containers/Proposal/state/sync-state"
import { formatInteger } from "~/utils/finance/format"

import { dialYearState } from "./MonthlyBillSlide"
import { DEVICE } from "../../../../constants/device-widths"
import {
  currentAfterSolarUtilityBillState,
  currentBeforeSolarUtilityBillState,
} from "../../state/brochure/brochure-state"
import { useProposalContext } from "../../state/proposal/proposal-context"

const PlaceholderText = styled.p`
  opacity: 0;
  @media ${DEVICE.tablet} {
    display: none;
  }
`

const PaymentSection = styled.div<{ before: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  margin-top: 170px;
  gap: clamp(5px, 1vw, 20px);
  width: fit-content;
  padding-top: 15px;
  @media ${DEVICE.tablet} {
    transform: none;
    margin-top: 0;
    width: 100%;
    padding-top: clamp(15px, 3vh, 25px);
  }
  @media print {
    background: white;
    margin-top: 300px;
  }
  > div {
    display: flex;
    flex-direction: column;
    gap: 10px;
    > div {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      gap: 5px;
      @media ${DEVICE.tablet} {
        flex-direction: row;
      }
      > p {
        color: var(--blue-dark-font);
        font-weight: 600;
        font-size: var(--step--1);
      }
    }
    h1 {
      font-size: var(--step-4);
      color: ${({ before }) => (before ? `#bc2626` : `var(--blue)`)};
      font-weight: 800;
      font-family: "Poppins", sans-serif;
    }
  }
  > h2 {
    font-size: var(--step-2);
    font-weight: 600;
    max-width: clamp(100px, 15vw, 400px);
    color: var(--blue-dark-font);
  }
`
const StyledBillCalculation = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  gap: clamp(5px, 2vw, 10px);
  h5 {
    color: var(--blue-dark-font);
    font-weight: 600;
    font-size: var(--step-0);
  }
  p {
    font-size: var(--step--1);
  }
  > div {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
`

const calculateMonthIndex = (dialYear: number, monthInYear = 1): number => {
  // Assuming dial year 1 corresponds to month 0 (first month)
  // Adjust this logic based on your business requirements
  return (dialYear - 1) * 12 + (monthInYear - 1)
}

const BillCalculation: FC<{ energy: string; fixed: string; total: string }> = ({
  energy,
  fixed,
  total,
}) => {
  const { t } = useTranslation()

  return (
    <StyledBillCalculation>
      <div>
        <p>{t(`billEnergy`)}</p>
        <h5>${energy}</h5>
      </div>
      {fixed && Number(fixed) > 0 && (
        <>
          <h5>+</h5>
          <div>
            <p>{t(`billFixed`)}</p>
            <h5>${fixed}</h5>
          </div>
          <h5>=</h5>
          <div>
            <p>{t(`billTotal`)}</p>
            <h5>${total}</h5>
          </div>
        </>
      )}
    </StyledBillCalculation>
  )
}

export const AverageUtilityBill: FC = () => {
  const { t } = useTranslation()
  const { energy, bill, fixed } = useRecoilValue(
    currentBeforeSolarUtilityBillState,
  ) ?? { energy: 0, bill: 0, fixed: 0 }
  const needsBrochureSync = useRecoilValue(syncWaitingForEntityState(`brochure`))
  const utilitySavingsPending = useRecoilValue(utilitySavingsPendingState)
  const needsSync = needsBrochureSync || utilitySavingsPending

  return (
    <PaymentSection before={true} aria-busy={needsSync}>
      <h2>{t(`averageUtilityBill`)}</h2>
      {!needsSync ? (
        <div id="brochure-data-is-current-utility">
          <h1>${formatInteger(bill)}</h1>
          <div>
            <PlaceholderText>{t(`newAverageUtilityBill`)}</PlaceholderText>
            <BillCalculation
              energy={formatInteger(energy)}
              fixed={formatInteger(fixed)}
              total={formatInteger(bill)}
            />
          </div>
        </div>
      ) : (
        <div style={{ margin: `2rem` }} aria-live="polite">
          <span className="sr-only">{t(`recalculatingUtilitySavings`)}</span>
          <SimpleLoader />
        </div>
      )}
    </PaymentSection>
  )
}

export const SolarPayment: FC = () => {
  const { t } = useTranslation()
  const { local } = useProposalContext()
  const {
    energy,
    bill,
    fixed,
    payment: defaultPayment,
  } = useRecoilValue(currentAfterSolarUtilityBillState) ?? {
    energy: 0,
    bill: 0,
    fixed: 0,
    payment: 0,
  }
  const needsBrochureSync = useRecoilValue(syncWaitingForEntityState(`brochure`))
  const utilitySavingsPending = useRecoilValue(utilitySavingsPendingState)
  const needsSync = needsBrochureSync || utilitySavingsPending
  const { years } = useRecoilValue(dialYearState)
  const {
    data: savings,
    isError: isSavingsError,
    isLoading: isSavingsLoading,
  } = useProposalSavings(local.id)
  const isSavingsPending = savings === undefined && !isSavingsError

  // Calculate payment based on finance type
  const calculatePayment = (): number => {
    const hasSchedule =
      (savings?.monthlyPaymentsByYear?.length ?? 0) > 0 ||
      (savings?.monthlyPayments?.length ?? 0) > 0

    const monthlyPaymentByYear = savings?.monthlyPaymentsByYear?.[years - 1]
    if (typeof monthlyPaymentByYear === `number`) {
      return monthlyPaymentByYear
    }

    const monthIndex = calculateMonthIndex(years)
    const monthlyPayment = savings?.monthlyPayments?.[monthIndex]

    if (typeof monthlyPayment === `number`) {
      return monthlyPayment
    }

    if (hasSchedule) {
      return 0
    }

    return defaultPayment ?? 0
  }

  const payment = calculatePayment()
  const isLoading = needsSync || isSavingsLoading || isSavingsPending

  return (
    <PaymentSection before={false}>
      <h2
        style={{
          opacity: 0,
        }}
      >
        {t(`averageUtilityBill`)}
      </h2>
      <h2
        style={{
          position: `absolute`,
        }}
      >
        {t(`solarPayment`)}
      </h2>
      {!isLoading ? (
        <div id="brochure-data-is-current-solar">
          <h1>${formatInteger(payment)}</h1>
          <div>
            <p>{t(`newAverageUtilityBill`)}:</p>
            <BillCalculation
              energy={formatInteger(energy)}
              fixed={formatInteger(fixed)}
              total={formatInteger(bill)}
            />
          </div>
        </div>
      ) : (
        <div style={{ margin: `2rem` }}>
          <SimpleLoader />
        </div>
      )}
    </PaymentSection>
  )
}
